(() => {
  let pendingVariantId;
  let upcartStyleInjected = false;
  let giftQuantityClampInProgress = false;
  let cleanupInProgress = false;
  let bxgyRepairInProgress = false;
  let bxgyRepairCooldownUntil = 0;
  let giftProtectUntil = 0; // block orphan cleanup right after ATC gift ensure
  let stableUiUntil = 0; // skip remount / drawer refresh that blinks images on iOS
  let refreshTimer = null;
  let lastRefreshAt = 0;
  let deferredMaintenanceTimer = null;
  let shadowGiftObserverBound = false;
  let upcartGiftApiBound = false;

  // UpCart V2 keeps cart UI in a shadow root — theme document queries miss it.
  const upcartRoot = () => window.upcartDocumentOrShadowRoot || document;

  // Always prefer Shopify cart.js for add/remove decisions (UpCart cache can be stale).
  const getShopifyCartStrict = () => {
    const cartUrl = window.routes?.cart_url || '/cart';
    return fetch(`${cartUrl}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => response.json());
  };

  const getCart = () => {
    // UI paint can use UpCart memory; mutations/ensure use strict cart.js.
    if (typeof window.upcartGetCart === 'function') {
      try {
        const upcartCart = window.upcartGetCart();
        if (upcartCart?.items) return Promise.resolve(upcartCart);
      } catch (error) { /* fall through */ }
    }
    return getShopifyCartStrict();
  };

  // Prevent decorateGiftLine() DOM inserts from re-entering MutationObservers.
  let uiPassActive = false;
  let uiTimer = null;
  let maintenanceTimer = null;

  const postCartJson = (url, body) => fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  }).then(async (response) => {
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.description || payload.message || `Cart request failed (${response.status})`);
    }
    return payload;
  });

  const cartAddUrl = () => (window.routes?.cart_add_url || '/cart/add').replace(/\.js$/, '') + '.js';
  const cartChangeUrl = () => (window.routes?.cart_change_url || '/cart/change').replace(/\.js$/, '') + '.js';
  const cartUpdateUrl = () => (window.routes?.cart_update_url || '/cart/update').replace(/\.js$/, '') + '.js';

  const refreshCartDrawers = ({ soft = false, force = false } = {}) => {
    const now = Date.now();
    // iPhone Safari re-decodes every <img> when UpCart remounts the drawer.
    // Collapse bursts of refresh (ATC + ensure + UpCart events) into one tick.
    if (!force && now - lastRefreshAt < 900) {
      window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(() => refreshCartDrawers({ soft, force: true }), 900);
      return;
    }
    lastRefreshAt = now;
    if (typeof window.upcartRefreshCart === 'function') {
      try { window.upcartRefreshCart(); } catch (error) { /* ignore */ }
    }
    // Soft / ATC-settle: don't broadcast theme events that re-render product media.
    if (soft || now < giftProtectUntil) return;
    if (window.publish && window.PUB_SUB_EVENTS) publish(PUB_SUB_EVENTS.cartUpdate, { source: 'free-gift-cart' });
    ['cart:refresh', 'cart:updated', 'upcart:refresh', 'upcart:cart:updated'].forEach((eventName) => {
      window.dispatchEvent(new CustomEvent(eventName));
      document.dispatchEvent(new CustomEvent(eventName));
    });
  };

  const wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

  const getConfiguredGiftIds = () => {
    const configuredSelectors = [...document.querySelectorAll('[data-free-gift-selector]')];
    return new Set(configuredSelectors.flatMap((selector) =>
      (selector.dataset.giftVariantIds || '').split(',').filter(Boolean)
    ));
  };

  const getConfiguredParentIds = () => {
    const configuredSelectors = [...document.querySelectorAll('[data-free-gift-selector]')];
    return new Set(configuredSelectors.flatMap((selector) =>
      (selector.dataset.parentVariantIds || '').split(',').filter(Boolean)
    ));
  };

  const isLinkedFreeGift = (item) => {
    if (!item) return false;
    if (item.properties?._free_gift === 'true') return true;
    if (String(item.properties?.['Free Gift Included'] || '').toLowerCase() === 'yes') return true;
    return false;
  };

  const isAiryssParentItem = (item) => {
    if (!item || isLinkedFreeGift(item)) return false;
    const title = String(item.product_title || item.title || '').toLowerCase();
    const handle = String(item.handle || item.product_handle || '').toLowerCase();
    const sku = String(item.sku || '').toUpperCase();
    if (/protection|warranty|portable|surface mount|free gift|magnetic/i.test(title)) return false;
    if (/free-gift|protection|portable|surface-mount|magnetic/i.test(handle)) return false;
    if (/airyss\s*one/.test(title)) return true;
    if (/airyss-one/.test(handle)) return true;
    if (/^AIRYSS-ONE/.test(sku)) return true;
    return false;
  };

  // Products that can receive the free-gift automatic discount (includes legacy
  // retail handles until $0 Free Gift SKUs are wired in Admin).
  const isGiftEligibleItem = (item, giftIds = getConfiguredGiftIds()) => {
    if (!item) return false;
    if (giftIds.has(String(item.variant_id))) return true;
    const handle = String(item.handle || item.product_handle || '').toLowerCase();
    const title = String(item.product_title || item.title || '').toLowerCase();
    if (/free-gift|portable-charger|portable-power-bank|airyss-surface-mount|magnetic-charging/.test(handle)) return true;
    if (/portable power bank|portable charger|surface mount|magnetic charging cable/.test(title)) return true;
    return false;
  };

  // Paid retail accessories must never be removed when AIRYSS One is deleted.
  const isRetailAccessoryOnly = (item) => {
    if (!item || isLinkedFreeGift(item)) return false;
    const handle = String(item.handle || item.product_handle || '').toLowerCase();
    return /airyss-surface-mount$|portable-charger-29|portable-power-bank$|magnetic-charging/.test(handle)
      && !/free-gift/.test(handle);
  };

  const isLineEffectivelyFree = (item) => {
    if (!item) return false;
    if (Number(item.final_line_price) === 0 || Number(item.final_price) === 0) return true;
    if (Number(item.total_discount) > 0 && Number(item.final_line_price) < Number(item.original_line_price)) {
      // Treat near-full gift discounts as free.
      return Number(item.final_line_price) <= 1;
    }
    return false;
  };

  // Gift / promo lines that must leave the cart when AIRYSS One is removed.
  const isRemovableGiftLine = (item) => {
    if (!item || isAiryssParentItem(item)) return false;
    if (isLinkedFreeGift(item)) return true;
    if (isRetailAccessoryOnly(item)) return false;
    if (isGiftEligibleItem(item) && isLineEffectivelyFree(item)) return true;
    const handle = String(item.handle || item.product_handle || '').toLowerCase();
    if (/free-gift/.test(handle)) return true;
    return false;
  };

  // Any free-gift-like line (linked ATC gift OR BXGY auto gift). Dupes inflate Cart · N.
  const isFreeGiftLike = (item) => {
    if (!item || isAiryssParentItem(item)) return false;
    if (isRetailAccessoryOnly(item) && !isLinkedFreeGift(item) && !isLineEffectivelyFree(item)) return false;
    return isRemovableGiftLine(item);
  };

  const giftBlurb = (item) => {
    const title = String(item.product_title || item.title || '').toLowerCase();
    if (/power bank|portable charger/.test(title)) return 'Backup power for your glasses and phone.';
    if (/surface mount/.test(title)) return 'Hands-free mounting for desk, car, or content capture.';
    if (/magnetic/.test(title)) return 'Keep your AIRYSS One™ powered on the go.';
    return 'Included free with your AIRYSS One™.';
  };

  const giftDisplayTitle = (item) => {
    const title = String(item.product_title || item.title || 'Free Gift');
    return title
      .replace(/\s*[-–—]\s*Free Gift$/i, '')
      .replace(/\s*\(\$?\d+.*?\)\s*/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const GIFT_CARD_ID = 'airyss-included-gift-card';
  const GIFT_WRAP_ID = 'airyss-included-gift-wrap';

  const ensureUpcartGiftStyles = () => {
    const root = upcartRoot();
    const host = root.head || root;
    if (!host?.appendChild) return;
    let style = host.querySelector?.('#airyss-free-gift-upcart-styles');
    if (style) return;
    style = document.createElement('style');
    style.id = 'airyss-free-gift-upcart-styles';
    host.appendChild(style);
    style.textContent = `
      #${GIFT_WRAP_ID} {
        list-style: none !important;
        display: block !important;
        order: 9998 !important;
        margin: 0 !important;
        padding: 0.15rem 0 !important;
        border: 0 !important;
        width: 100% !important;
        background: transparent !important;
      }
      #${GIFT_CARD_ID} {
        margin: 8px 12px;
        padding: 14px;
        border: 1px solid #ebebeb;
        border-radius: 16px;
        background: #fff;
        color: #111;
        box-sizing: border-box;
        font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      }
      #${GIFT_CARD_ID} .airyss-included-gift-label {
        display: block;
        font-size: 14px;
        font-weight: 600;
        color: #888;
        margin: 0 0 12px;
      }
      #${GIFT_CARD_ID} .airyss-included-gift-body {
        display: grid;
        grid-template-columns: 72px 1fr;
        gap: 12px;
        align-items: start;
      }
      #${GIFT_CARD_ID} .airyss-included-gift-image {
        width: 72px;
        height: 72px;
        border-radius: 12px;
        object-fit: contain;
        background: #f6f6f6;
        display: block;
        -webkit-backface-visibility: hidden;
        backface-visibility: hidden;
      }
      #${GIFT_CARD_ID} .airyss-included-gift-image-fallback {
        width: 72px;
        height: 72px;
        border-radius: 12px;
        background: #f6f6f6;
      }
      #${GIFT_CARD_ID} .airyss-included-gift-title {
        margin: 0;
        font-size: 16px;
        font-weight: 700;
        line-height: 1.3;
        letter-spacing: .01em;
      }
      #${GIFT_CARD_ID} .airyss-included-gift-desc {
        margin: .3rem 0 0;
        font-size: 14px;
        line-height: 1.4;
        color: #666;
      }
      #${GIFT_CARD_ID} .airyss-included-gift-meta {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: .75rem;
        margin-top: .7rem;
      }
      #${GIFT_CARD_ID} .airyss-included-gift-free-badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 999px;
        background: #111;
        color: #fff;
        font-size: 12px;
        font-weight: 800;
        letter-spacing: .04em;
        padding: .3rem .8rem;
      }
      .airyss-native-gift-line-hidden {
        display: none !important;
        height: 0 !important;
        margin: 0 !important;
        padding: 0 !important;
        overflow: hidden !important;
        border: 0 !important;
        visibility: hidden !important;
        pointer-events: none !important;
      }
      /* Hide free-gift SKUs in the UpCart list; the designed card is shown instead */
      .upcart-public-cart-item:has(a[href*="free-gift"]),
      .upcart-internal-cart-item:has(a[href*="free-gift"]),
      .upcart-public-component-product-tile:has(a[href*="free-gift"]) {
        display: none !important;
        height: 0 !important;
        margin: 0 !important;
        padding: 0 !important;
        overflow: hidden !important;
        border: 0 !important;
        visibility: hidden !important;
        pointer-events: none !important;
      }
      /* Keep UpCart drawer close (X) visible in the header */
      [class*="header"] button[aria-label*="close" i],
      [class*="Header"] button[aria-label*="close" i],
      [class*="header"] button[aria-label*="Close" i],
      [class*="Header"] [class*="close" i],
      [class*="header"] [class*="Close" i],
      button.upcart-public-cart-close,
      [class*="CartClose"],
      [class*="cart-close"],
      [class*="drawer"] > button[aria-label*="close" i] {
        display: inline-flex !important;
        visibility: visible !important;
        opacity: 1 !important;
        pointer-events: auto !important;
        z-index: 30 !important;
      }
    `;
    upcartStyleInjected = true;
  };

  const queryAllDeep = (selector) => {
    const root = upcartRoot();
    const fromRoot = [...(root.querySelectorAll?.(selector) || [])];
    if (root === document) return fromRoot;
    return [...new Set([...fromRoot, ...document.querySelectorAll(selector)])];
  };

  const hardHideNativeGiftLine = (node) => {
    if (!node) return;
    if (node.id === GIFT_WRAP_ID || node.id === GIFT_CARD_ID) return;
    if (node.id === 'airyss-protection-card-wrap' || node.id === 'airyss-protection-card') return;
    if (node.hasAttribute?.('data-airyss-included-gift-wrap') || node.hasAttribute?.('data-airyss-protection-wrap')) return;
    node.classList.add('airyss-native-gift-line-hidden');
    node.style.setProperty('display', 'none', 'important');
    node.setAttribute('aria-hidden', 'true');
  };

  const hideNativeGiftLines = (cart) => {
    const linked = (cart?.items || []).filter(isLinkedFreeGift);
    const titles = linked.map((item) => String(item.product_title || item.title || '').toLowerCase()).filter(Boolean);
    const variantIds = linked.map((item) => String(item.variant_id)).filter(Boolean);

    queryAllDeep('.upcart-public-cart-item, .upcart-internal-cart-item, .upcart-public-component-product-tile').forEach((node) => {
      if (node.id === GIFT_WRAP_ID || node.contains?.(document.getElementById(GIFT_CARD_ID))) return;
      if (node.querySelector?.(`#${GIFT_CARD_ID}, #airyss-protection-card, [data-protection-checkbox]`)) return;
      const text = (node.textContent || '').toLowerCase();
      const html = node.innerHTML || '';
      const byProp = /free gift included/i.test(text);
      const byTitle = titles.some((title) => title && text.includes(title));
      const byId = variantIds.some((id) => id && html.includes(id));
      const looksLikeGiftRow = byProp
        || /free gift with airyss/i.test(text)
        || (byId && byTitle && !/airyss\s*one/i.test(text));
      // Hide native UpCart row for the linked gift only — never hide paid retail accessories
      // (paid lines do not include the Free Gift Included property).
      if (looksLikeGiftRow && (byProp || byId || byTitle) && !/add 2-year|protection plan\s*\+/.test(text)) {
        hardHideNativeGiftLine(node);
      }
    });
  };

  const ensureGiftCard = () => {
    const root = upcartRoot();
    let card = root.getElementById?.(GIFT_CARD_ID) || document.getElementById(GIFT_CARD_ID);
    if (card) return card;
    card = document.createElement('div');
    card.id = GIFT_CARD_ID;
    card.setAttribute('data-airyss-included-gift-card', 'true');
    card.innerHTML = `
      <div class="airyss-included-gift-label">Included Free Gift</div>
      <div class="airyss-included-gift-body">
        <div data-gift-media></div>
        <div>
          <h3 class="airyss-included-gift-title" data-gift-title></h3>
          <p class="airyss-included-gift-desc" data-gift-desc></p>
          <div class="airyss-included-gift-meta">
            <span class="airyss-included-gift-free-badge">FREE</span>
          </div>
        </div>
      </div>
    `;
    return card;
  };

  const ensureGiftWrapper = (card) => {
    const root = upcartRoot();
    let wrap = root.getElementById?.(GIFT_WRAP_ID) || document.getElementById(GIFT_WRAP_ID);
    if (!wrap) {
      wrap = document.createElement('div');
      wrap.id = GIFT_WRAP_ID;
      wrap.setAttribute('data-airyss-included-gift-wrap', 'true');
    }
    if (card.parentElement !== wrap) wrap.appendChild(card);
    return wrap;
  };

  const placeGiftCard = (card) => {
    const root = upcartRoot();
    ensureUpcartGiftStyles();
    const wrap = ensureGiftWrapper(card);
    const protectionWrap = root.getElementById?.('airyss-protection-card-wrap')
      || document.getElementById('airyss-protection-card-wrap');
    const bottomModules = root.querySelector?.('[class*="bottomModules"], [class*="BottomModules"]')
      || document.querySelector('[class*="bottomModules"], [class*="BottomModules"]');
    const list = root.querySelector?.('.upcart-public-cart-items')
      || document.querySelector('.upcart-public-cart-items');

    // Always sit above the protection checkbox card.
    if (protectionWrap?.parentElement) {
      if (wrap.nextElementSibling !== protectionWrap || wrap.parentElement !== protectionWrap.parentElement) {
        protectionWrap.insertAdjacentElement('beforebegin', wrap);
      }
      return true;
    }
    if (bottomModules?.parentElement) {
      if (wrap.nextElementSibling !== bottomModules || wrap.parentElement !== bottomModules.parentElement) {
        bottomModules.insertAdjacentElement('beforebegin', wrap);
      }
      return true;
    }
    if (list) {
      if (wrap.parentElement !== list || list.lastElementChild !== wrap) list.appendChild(wrap);
      return true;
    }
    // UpCart still mounting — retry quickly so the card doesn't appear late.
    window.setTimeout(() => {
      if (lastGiftCart) renderGiftCard(lastGiftCart);
    }, 50);
    return false;
  };

  const renderGiftCard = (cart) => {
    const items = cart?.items || [];
    const hasParent = items.some(isAiryssParentItem);
    const linked = items.find(isLinkedFreeGift);
    const root = upcartRoot();
    const existingWrap = root.getElementById?.(GIFT_WRAP_ID) || document.getElementById(GIFT_WRAP_ID);

    // Never show the Included Free Gift card without AIRYSS One in the cart.
    if (!hasParent || !linked) {
      // Stale UpCart snapshots right after ATC can omit the gift for a beat.
      // Removing the card here makes iPhone images blink while the cart settles.
      if (Date.now() < giftProtectUntil || Date.now() < stableUiUntil) {
        hideNativeGiftLines(cart);
        return;
      }
      existingWrap?.remove();
      return;
    }

    const card = ensureGiftCard();
    const title = giftDisplayTitle(linked);
    const desc = giftBlurb(linked);
    const image = linked.featured_image?.url
      || linked.image
      || linked.featured_image
      || '';

    const titleEl = card.querySelector('[data-gift-title]');
    const descEl = card.querySelector('[data-gift-desc]');
    if (titleEl && titleEl.textContent !== title) titleEl.textContent = title;
    if (descEl && descEl.textContent !== desc) descEl.textContent = desc;
    card.querySelector('[data-gift-value]')?.remove();

    const media = card.querySelector('[data-gift-media]');
    if (media) {
      const src = image ? String(image) : '';
      const existingImg = media.querySelector('img.airyss-included-gift-image');
      if (src) {
        if (existingImg) {
          // Reuse the same <img> — replacing innerHTML reloads the file on iOS.
          if (existingImg.getAttribute('src') !== src) existingImg.src = src;
        } else {
          media.replaceChildren();
          const img = document.createElement('img');
          img.className = 'airyss-included-gift-image';
          img.src = src;
          img.alt = '';
          img.width = 72;
          img.height = 72;
          img.decoding = 'async';
          img.loading = 'eager';
          media.appendChild(img);
        }
      } else if (!media.querySelector('.airyss-included-gift-image-fallback')) {
        media.innerHTML = '<div class="airyss-included-gift-image-fallback" aria-hidden="true"></div>';
      }
    }

    const wrap = upcartRoot().getElementById?.(GIFT_WRAP_ID) || document.getElementById(GIFT_WRAP_ID);
    if (Date.now() < stableUiUntil && wrap?.isConnected && wrap.contains(card)) {
      hideNativeGiftLines(cart);
      return;
    }

    placeGiftCard(card);
    hideNativeGiftLines(cart);
  };

  let lastGiftCart = null;

  const readUpcartCartSync = () => {
    if (typeof window.upcartGetCart !== 'function') return null;
    try {
      const cart = window.upcartGetCart();
      return cart?.items ? cart : null;
    } catch (error) {
      return null;
    }
  };

  const hideFreeGiftQuantityControls = (cart) => {
    if (uiPassActive) return;
    uiPassActive = true;
    try {
      ensureUpcartGiftStyles();
      const source = cart?.items ? cart : (readUpcartCartSync() || lastGiftCart);
      if (source?.items) {
        lastGiftCart = source;
        renderGiftCard(source);
        syncCartCountBadge(source);
      } else {
        getCart().then((fresh) => {
          lastGiftCart = fresh;
          renderGiftCard(fresh);
          syncCartCountBadge(fresh);
        }).catch(() => {});
      }
    } finally {
      uiPassActive = false;
    }
  };

  const scheduleGiftUi = (cart, delay = 0) => {
    window.clearTimeout(uiTimer);
    uiTimer = window.setTimeout(() => hideFreeGiftQuantityControls(cart), delay);
  };

  const setLineQuantity = (line, quantity) => {
    const body = { quantity };
    if (line?.key) body.id = line.key;
    else if (line?.variant_id != null) body.id = line.variant_id;
    else if (line?.id != null) body.id = line.id;
    else return Promise.reject(new Error('Missing cart line id'));
    return postCartJson(cartChangeUrl(), body);
  };

  const changeLines = (lines) => {
    if (!lines?.length) return Promise.resolve([]);
    const updates = {};
    lines.forEach((line) => {
      if (line.key) updates[line.key] = 0;
      else if (line.variant_id != null) updates[String(line.variant_id)] = 0;
      else if (line.id != null) updates[String(line.id)] = 0;
    });
    if (Object.keys(updates).length) {
      return postCartJson(cartUpdateUrl(), { updates }).catch(() =>
        Promise.all(lines.map((line) => setLineQuantity(line, 0)))
      );
    }
    return Promise.all(lines.map((line) => setLineQuantity(line, 0)));
  };

  const clampFreeGiftQuantities = () => {
    if (giftQuantityClampInProgress || !window.routes?.cart_url) return;
    giftQuantityClampInProgress = true;
    getCart()
      .then((cart) => {
        const oversizedGifts = cart.items.filter((item) =>
          isLinkedFreeGift(item) && item.quantity > 1
        );
        return Promise.all(oversizedGifts.map((item) => setLineQuantity(item, 1))).then((results) => {
          if (oversizedGifts.length) refreshCartDrawers({ soft: true });
          return results;
        });
      })
      .catch((error) => console.error('Free gift quantity clamp failed:', error))
      .finally(() => { giftQuantityClampInProgress = false; });
  };

  const addGift = (giftVariantId, parentVariantId) => postCartJson(cartAddUrl(), {
    items: [{
      id: Number(giftVariantId),
      quantity: 1,
      properties: {
        _free_gift: 'true',
        _gift_for: String(parentVariantId),
        'Free Gift Included': 'Yes',
      },
    }],
  });

  const snapshotLineForReadd = (item) => ({
    id: Number(item.variant_id),
    quantity: item.quantity || 1,
    properties: Object.fromEntries(
      Object.entries(item.properties || {}).filter(([key]) => key && !key.startsWith('_'))
    ),
  });

  /**
   * BXGY "get 1 free from list" often sticks to the most recently added eligible
   * gift product. When a customer later adds a retail accessory from that same
   * list, the automatic discount can jump onto the paid line and leave the
   * linked free-gift line at full price. Repair by clearing paid gift lines,
   * re-adding them, then re-adding the linked free gift LAST so it keeps FREE.
   */
  const repairBxgyDiscountConflict = () => {
    if (bxgyRepairInProgress || !window.routes?.cart_url) return Promise.resolve(false);
    if (Date.now() < bxgyRepairCooldownUntil) return Promise.resolve(false);
    bxgyRepairInProgress = true;

    return getCart()
      .then(async (cart) => {
        const giftIds = getConfiguredGiftIds();
        const linkedGifts = cart.items.filter(isLinkedFreeGift);
        if (!linkedGifts.length) return false;

        const paidGiftLines = cart.items.filter((item) =>
          !isLinkedFreeGift(item) && isGiftEligibleItem(item, giftIds)
        );
        if (!paidGiftLines.length) return false;

        const linkedNotFree = linkedGifts.filter((item) => !isLineEffectivelyFree(item));
        const paidStealingDiscount = paidGiftLines.filter((item) => isLineEffectivelyFree(item));
        if (!linkedNotFree.length || !paidStealingDiscount.length) return false;

        const paidSnapshots = paidGiftLines.map(snapshotLineForReadd);
        const giftSnapshots = linkedGifts.map((item) => ({
          id: Number(item.variant_id),
          quantity: 1,
          properties: {
            _free_gift: 'true',
            _gift_for: String(item.properties?._gift_for || ''),
            'Free Gift Included': 'Yes',
          },
        }));

        const updates = {};
        [...paidGiftLines, ...linkedGifts].forEach((item) => {
          if (item.key) updates[item.key] = 0;
        });
        await postCartJson(cartUpdateUrl(), { updates });
        await wait(350);

        // Re-add paid accessories first (full price), then linked free gift last
        // so Shopify's BXGY attaches to the most recently added eligible item.
        if (paidSnapshots.length) {
          await postCartJson(cartAddUrl(), { items: paidSnapshots });
          await wait(250);
        }
        if (giftSnapshots.length) {
          await postCartJson(cartAddUrl(), { items: giftSnapshots });
          await wait(350);
        }

        // Avoid repair loops if Shopify still reassigns the discount.
        bxgyRepairCooldownUntil = Date.now() + 8000;
        refreshCartDrawers({ soft: true });
        return true;
      })
      .catch((error) => {
        console.error('BXGY free-gift conflict repair failed:', error);
        bxgyRepairCooldownUntil = Date.now() + 5000;
        return false;
      })
      .finally(() => {
        bxgyRepairInProgress = false;
      });
  };

  const resolveSelectedGiftVariantId = (form) => {
  if (!form) return '';

  if (form.dataset.freeGiftVariantId) {
    return form.dataset.freeGiftVariantId;
  }

  const selector =
    document.querySelector(`[data-free-gift-selector][data-form-id="${form.id}"]`)
    || document.querySelector('[data-free-gift-selector]');

  if (!selector) return '';

  let selectedOption =
    selector.querySelector('.free-gift-option.is-selected');

  // Default to the first gift option — Portable Power Bank
  if (!selectedOption) {
    selectedOption =
      selector.querySelector('.free-gift-option');

    if (selectedOption) {
      selectedOption.classList.add('is-selected');

      selector.dataset.selectedGiftVariantId =
        selectedOption.dataset.giftVariantId || '';

      form.dataset.freeGiftVariantId =
        selectedOption.dataset.giftVariantId || '';
    }
  }

  return (
    selector.dataset.selectedGiftVariantId
    || selectedOption?.dataset?.giftVariantId
    || ''
  );
};document.addEventListener('DOMContentLoaded', () => {
  const selector =
    document.querySelector('[data-free-gift-selector]');

  if (!selector) return;

  const form =
    document.getElementById(selector.dataset.formId)
    || document.querySelector('product-form form');

  resolveSelectedGiftVariantId(form);
});

  const addSelectedGiftIfMissing = (form) => {
    if (!form) return;
    if (form.dataset.freeGiftEnsuring === 'true') return;

    const giftVariantId = resolveSelectedGiftVariantId(form);
    // Read the live variant input so changing a product option after choosing a
    // gift cannot link the gift to an old variant.
    const parentVariantId = form.querySelector('[name="id"], [name="items[0][id]"]')?.value || form.dataset.freeGiftParentId;
    if (!giftVariantId || !parentVariantId) return;

    form.dataset.freeGiftVariantId = String(giftVariantId);
    form.dataset.freeGiftParentId = String(parentVariantId);
    form.dataset.freeGiftEnsuring = 'true';
    holdVisibleCartCount(expectedCountAfterAtc(form), 4500);
    giftProtectUntil = Date.now() + 5000;
    stableUiUntil = Date.now() + 2500;
    const complete = () => delete form.dataset.freeGiftEnsuring;

    // UpCart often adds only the main line. Poll Shopify cart.js until parent
    // exists, then add the linked free gift if missing.
    let attempts = 0;
    const ensureGift = () => {
      getShopifyCartStrict().then((cart) => {
        const parentInCart = cart.items.some((item) =>
          String(item.variant_id) === String(parentVariantId) && !isLinkedFreeGift(item)
        );
        if (!parentInCart) {
          attempts += 1;
          if (attempts < 16) window.setTimeout(ensureGift, 250);
          else complete();
          return;
        }

        const linkedGifts = cart.items.filter((item) =>
          isLinkedFreeGift(item) && String(item.properties?._gift_for) === String(parentVariantId)
        );
        // Dual-add / BXGY may already have the gift variant without our properties.
        const sameVariantGift = cart.items.find((item) =>
          String(item.variant_id) === String(giftVariantId) && isFreeGiftLike(item)
        );
        const unwantedGifts = linkedGifts.filter((item) => String(item.variant_id) !== String(giftVariantId));
        const selectedGift = linkedGifts.find((item) => String(item.variant_id) === String(giftVariantId))
          || sameVariantGift;

        // Never add a second gift when one free-gift-like line already exists.
        const anyFreeGift = cart.items.some(isFreeGiftLike);

        return changeLines(unwantedGifts)
          .then(() => {
            if (selectedGift) {
              if (selectedGift.quantity > 1) {
                return setLineQuantity(selectedGift, 1).then(() => true);
              }
              return unwantedGifts.length > 0;
            }
            if (anyFreeGift) return unwantedGifts.length > 0;
            return addGift(giftVariantId, parentVariantId).then(() => true);
          })
          .then((mutated) => dedupeFreeGifts().then((deduped) => Boolean(mutated || deduped)))
          .then((mutated) => {
            giftProtectUntil = Date.now() + 4000;
            stableUiUntil = Date.now() + 2500;
            // Gift is already in the first ATC payload — a drawer refresh remounts
            // every cart image and looks like a blink on iPhone Safari.
            if (mutated) refreshCartDrawers({ soft: true });
            return getShopifyCartStrict();
          })
          .then((fresh) => {
            lastGiftCart = fresh;
            scheduleGiftUi(fresh, 0);
            syncCartCountBadge(fresh);
            complete();
          });
      }).catch((error) => {
        complete();
        console.error('Free gift add failed:', error);
      });
    };
    window.setTimeout(ensureGift, 80);
  };

  // Keep at most one free gift per AIRYSS One parent (fixes Cart · 3 when only 2 products show).
  const dedupeFreeGifts = () => {
    if (Date.now() < giftProtectUntil && cleanupInProgress) return Promise.resolve(false);
    return getShopifyCartStrict().then((cart) => {
      const parents = cart.items.filter(isAiryssParentItem);
      const freeGifts = cart.items.filter(isFreeGiftLike);
      if (freeGifts.length <= 1) {
        syncCartCountBadge(cart);
        return false;
      }

      const keepKeys = new Set();
      const selector = document.querySelector('[data-free-gift-selector]');
      const preferredVariant = selector?.dataset.selectedGiftVariantId
        || document.querySelector('product-form form')?.dataset?.freeGiftVariantId
        || '';

      if (parents.length) {
        parents.forEach((parent) => {
          const forParent = freeGifts.filter((gift) =>
            String(gift.properties?._gift_for) === String(parent.variant_id)
          );
          const pool = forParent.length ? forParent : freeGifts.filter((gift) => !keepKeys.has(gift.key));
          const pick = pool.find((gift) => String(gift.variant_id) === String(preferredVariant))
            || pool.find(isLinkedFreeGift)
            || pool[0];
          if (pick?.key) keepKeys.add(pick.key);
          else if (pick) keepKeys.add(`vid:${pick.variant_id}`);
        });
      } else {
        // No parent — orphan cleanup will strip gifts; don't keep any here.
      }

      const extras = freeGifts.filter((gift) => {
        if (gift.key && keepKeys.has(gift.key)) return false;
        if (keepKeys.has(`vid:${gift.variant_id}`)) return false;
        return true;
      });
      if (!extras.length) {
        syncCartCountBadge(cart);
        return false;
      }

      return changeLines(extras).then(() => getShopifyCartStrict()).then((fresh) => {
        lastGiftCart = fresh;
        scheduleGiftUi(fresh, 0);
        syncCartCountBadge(fresh);
        refreshCartDrawers({ soft: true });
        return true;
      });
    }).catch((error) => {
      console.error('Free gift dedupe failed:', error);
      return false;
    });
  };

  // Cart · N badge should match visible products (1 gift max, + protection, + paid lines).
  const displayCartCount = (cart) => {
    const items = cart?.items || [];
    let count = 0;
    let countedGift = false;
    items.forEach((item) => {
      const qty = Number(item.quantity) || 1;
      if (isFreeGiftLike(item)) {
        if (!countedGift) {
          count += 1;
          countedGift = true;
        }
        return;
      }
      count += qty;
    });
    return count;
  };

  const syncCartCountBadge = (cart) => {
    if (window.__airyssCartCountHoldUntil && Date.now() < window.__airyssCartCountHoldUntil) {
      if (window.__airyssCartCountHoldValue != null) {
        applyCartCountBadge(window.__airyssCartCountHoldValue);
      }
      return;
    }
    applyCartCountBadge(displayCartCount(cart));
  };

  const applyCartCountBadge = (count, { skipClose = false } = {}) => {
    const n = Math.max(0, Number(count) || 0);
    const roots = [upcartRoot(), document];
    const cartLabelRe = /^(Cart|Bag)\s*([·•.\-–—])\s*\d+\s*$/i;
    roots.forEach((root) => {
      if (!root?.querySelectorAll) return;
      // Never rewrite a header container — that destroys the UpCart close (X) button.
      // Only update a leaf text node / element whose entire text is "Bag · N" / "Cart · N".
      root.querySelectorAll('h1, h2, h3, span, p, div, [class*="cart-count"], [class*="CartCount"], [class*="title"], [class*="Title"]').forEach((el) => {
        if (el.closest?.('#airyss-protection-card, #airyss-included-gift-card')) return;
        if (el.querySelector?.('button, a, svg, img, input, [aria-label*="close" i], [class*="close" i], [class*="Close"]')) return;
        if (el.children?.length) return; // leaf only — preserves sibling close button
        const text = (el.textContent || '').trim();
        if (!cartLabelRe.test(text)) return;
        const next = text.replace(/\d+\s*$/, String(n));
        if (next !== text) el.textContent = next;
      });
      // Fallback: update only the trailing digit text node inside a title row.
      root.querySelectorAll('[class*="header"], [class*="Header"]').forEach((header) => {
        if (!header || header.closest?.('#airyss-protection-card')) return;
        if (root === document && !header.closest?.('.upcart-cart, [class*="upcart"]')) return;
        const walker = document.createTreeWalker(header, NodeFilter.SHOW_TEXT);
        let node = walker.nextNode();
        while (node) {
          const text = String(node.nodeValue || '').trim();
          if (cartLabelRe.test(text) && node.parentElement
            && !node.parentElement.querySelector?.('button, svg, [aria-label*="close" i]')) {
            const next = String(node.nodeValue).replace(/\d+\s*$/, String(n));
            if (next !== node.nodeValue) node.nodeValue = next;
          } else if (/^\d+$/.test(text) && /(cart|bag)/i.test(String(node.parentElement?.textContent || ''))
            && node.parentElement?.children?.length === 0) {
            // bare count next to "Bag" / "Cart" label
            const prev = (node.parentElement?.previousSibling?.textContent
              || node.previousSibling?.textContent || '').toLowerCase();
            if (/(cart|bag)/.test(prev) || /(cart|bag)/i.test(node.parentElement?.parentElement?.textContent || '')) {
              if (String(node.nodeValue).trim() !== String(n)) node.nodeValue = String(n);
            }
          }
          node = walker.nextNode();
        }
      });
      root.querySelectorAll('.cart-count-bubble span[aria-hidden="true"], [data-cart-count]').forEach((el) => {
        if (el.querySelector?.('button, svg')) return;
        if (/^\d+$/.test((el.textContent || '').trim()) && !el.children?.length) el.textContent = String(n);
      });
    });
    window.__airyssDisplayCartCount = n;
    if (!skipClose) ensureUpcartCloseVisible();
  };

  // Make sure UpCart's header close control stays visible after our count sync.
  const ensureUpcartCloseVisible = () => {
    const root = upcartRoot();
    if (!root?.querySelectorAll) return;
    // Never inject a close control into the storefront header.
    if (root === document) {
      document.getElementById('airyss-upcart-close')?.remove();
      return;
    }

    let nativeClose = null;
    root.querySelectorAll('button, [role="button"], a').forEach((el) => {
      const label = `${el.getAttribute('aria-label') || ''} ${el.getAttribute('title') || ''} ${el.className || ''}`;
      const looksClose = /close|dismiss/i.test(label) || ((el.textContent || '').trim() === '×' || (el.textContent || '').trim() === '✕');
      if (!looksClose && !(/close/i.test(label) && el.querySelector?.('svg'))) return;
      if (el.closest?.('.upcart-public-cart-item, .upcart-internal-cart-item, [class*="CartItem"], [class*="product-tile"]')) return;
      const inHeader = el.closest?.('[class*="header"], [class*="Header"]') || el.parentElement;
      if (!inHeader) return;
      nativeClose = el;
      el.style.setProperty('display', 'inline-flex', 'important');
      el.style.setProperty('visibility', 'visible', 'important');
      el.style.setProperty('opacity', '1', 'important');
      el.style.setProperty('pointer-events', 'auto', 'important');
      el.style.setProperty('z-index', '30', 'important');
    });

    // If a prior count sync wiped the native X, inject a reliable fallback.
    const header = root.querySelector?.('[class*="header"], [class*="Header"]');
    if (!header) return;
    let fallback = root.getElementById?.('airyss-upcart-close') || document.getElementById('airyss-upcart-close');
    if (nativeClose) {
      fallback?.remove();
      return;
    }
    if (!fallback) {
      fallback = document.createElement('button');
      fallback.id = 'airyss-upcart-close';
      fallback.type = 'button';
      fallback.setAttribute('aria-label', 'Close cart');
      fallback.textContent = '×';
      fallback.style.cssText = [
        'position:absolute',
        'top:10px',
        'right:12px',
        'width:36px',
        'height:36px',
        'border:0',
        'background:transparent',
        'color:#111',
        'font-size:28px',
        'line-height:1',
        'cursor:pointer',
        'z-index:40',
        'display:inline-flex',
        'align-items:center',
        'justify-content:center',
        'padding:0',
      ].join(';');
      fallback.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (typeof window.upcartCloseCart === 'function') {
          try { window.upcartCloseCart(); } catch (error) { /* ignore */ }
        }
      });
    }
    const headerStyle = window.getComputedStyle?.(header);
    if (headerStyle && headerStyle.position === 'static') {
      header.style.position = 'relative';
    }
    if (fallback.parentElement !== header) header.appendChild(fallback);
  };

  let countHoldTicker = null;
  const keepHeldCountVisible = () => {
    if (countHoldTicker) return;
    const tick = () => {
      if (!window.__airyssCartCountHoldUntil || Date.now() >= window.__airyssCartCountHoldUntil) {
        countHoldTicker = null;
        const cart = readUpcartCartSync() || lastGiftCart;
        if (cart) applyCartCountBadge(displayCartCount(cart));
        return;
      }
      applyCartCountBadge(window.__airyssCartCountHoldValue, { skipClose: true });
      countHoldTicker = window.requestAnimationFrame(tick);
    };
    countHoldTicker = window.requestAnimationFrame(tick);
  };

  const expectedCountAfterAtc = (form) => {
    const cart = readUpcartCartSync() || lastGiftCart;
    const items = cart?.items || [];
    const qty = Math.max(1, Number(form?.querySelector('[name="quantity"]')?.value) || 1);
    const next = displayCartCount(cart) + qty + (items.some(isFreeGiftLike) ? 0 : 1);
    return Math.max(1, next);
  };

  const holdVisibleCartCount = (count, ms = 4500) => {
    window.__airyssCartCountHoldValue = count;
    window.__airyssCartCountHoldUntil = Date.now() + ms;
    applyCartCountBadge(count);
    keepHeldCountVisible();
  };

  window.AiryssCartCount = {
    sync: syncCartCountBadge,
    displayCount: displayCartCount,
    hold: (count, ms = 2000) => holdVisibleCartCount(count, ms),
    release: () => {
      window.__airyssCartCountHoldUntil = 0;
    },
  };

  // Only re-add a gift UpCart dropped when the customer already chose one on the PDP.
  const ensureGiftForOpenCart = () => {
    if (Date.now() < giftProtectUntil) {
      const settling = readUpcartCartSync() || lastGiftCart;
      if (settling?.items) {
        lastGiftCart = settling;
        scheduleGiftUi(settling, 0);
        syncCartCountBadge(settling);
      }
      return;
    }
    const selector = document.querySelector('[data-free-gift-selector]');
    const form = selector ? document.getElementById(selector.dataset.formId) : document.querySelector('product-form form');
    const giftVariantId = resolveSelectedGiftVariantId(form);
    if (!giftVariantId) {
      getShopifyCartStrict().then((cart) => {
        syncCartCountBadge(cart);
        return dedupeFreeGifts();
      }).catch(() => {});
      return;
    }

    getShopifyCartStrict().then((cart) => {
      const parents = cart.items.filter(isAiryssParentItem);
      if (!parents.length) return;
      // If any free gift already exists, only dedupe — never add another (prevents Cart · 3).
      if (cart.items.some(isFreeGiftLike)) {
        lastGiftCart = cart;
        scheduleGiftUi(cart, 0);
        syncCartCountBadge(cart);
        return dedupeFreeGifts();
      }
      const missingParents = parents.filter((parent) => !cart.items.some((item) =>
        isLinkedFreeGift(item) && String(item.properties?._gift_for) === String(parent.variant_id)
      ));
      if (!missingParents.length) {
        lastGiftCart = cart;
        scheduleGiftUi(cart, 0);
        syncCartCountBadge(cart);
        return;
      }
      giftProtectUntil = Date.now() + 5000;
      return missingParents.reduce(
        (promise, parent) => promise.then(() => addGift(giftVariantId, parent.variant_id)),
        Promise.resolve()
      ).then(() => {
        refreshCartDrawers({ soft: true });
        return getShopifyCartStrict();
      }).then((fresh) => {
        lastGiftCart = fresh;
        scheduleGiftUi(fresh, 0);
        syncCartCountBadge(fresh);
        return dedupeFreeGifts();
      });
    }).catch((error) => console.error('Free gift cart ensure failed:', error));
  };

  const removeOrphanedGift = () => {
    const variantId = pendingVariantId;
    pendingVariantId = undefined;
    if (!variantId || !window.routes?.cart_url) return;

    getCart()
      .then((cart) => {
        const hasQualifyingProduct = cart.items.some((item) =>
          String(item.variant_id) === String(variantId) && !isLinkedFreeGift(item)
        );
        if (hasQualifyingProduct) return;

        const orphanedGifts = cart.items.filter((item) =>
          isLinkedFreeGift(item) && String(item.properties?._gift_for) === String(variantId)
        );
        return Promise.all(orphanedGifts.map((gift) => setLineQuantity(gift, 0))).then(() => {
          if (orphanedGifts.length && window.publish && window.PUB_SUB_EVENTS) {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'free-gift-cart' });
          }
        });
      })
      .catch((error) => console.error('Free gift cart cleanup failed:', error));
  };

  const scheduleCleanup = (target) => {
    const row = target.closest?.('.cart-item');
    if (!row || row.dataset.freeGiftFor) return;
    pendingVariantId = row.dataset.variantId;
    window.setTimeout(removeOrphanedGift, 450);
  };

  // Capture at window level: cart-drawer apps often stop the click at the
  // document level before the theme's normal form handler can see it.
  window.addEventListener('click', (event) => {
    const submitButton = event.target.closest?.('[type="submit"]');
    const productForm = submitButton?.closest?.('product-form');
    if (productForm) {
      const form = productForm.querySelector('form');
      if (resolveSelectedGiftVariantId(form)) holdVisibleCartCount(expectedCountAfterAtc(form), 4500);
      addSelectedGiftIfMissing(form);
    }
  }, true);

  document.addEventListener('submit', (event) => {
    if (event.target.matches?.('form')) addSelectedGiftIfMissing(event.target);
  }, true);

  document.addEventListener('click', (event) => {
    if (event.target.closest?.('cart-remove-button')) scheduleCleanup(event.target);
    if (pathHasUpcartRemove(event) || event.target.closest?.('.upcart-product-item, .upcart-public-cart-items, [class*="upcart"]')) {
      window.setTimeout(cleanupAllOrphanedGifts, 100);
      window.setTimeout(cleanupAllOrphanedGifts, 450);
      window.setTimeout(cleanupAllOrphanedGifts, 1000);
    }
  }, true);
  window.addEventListener('click', (event) => {
    if (!pathHasUpcartRemove(event)) return;
    window.setTimeout(cleanupAllOrphanedGifts, 100);
    window.setTimeout(cleanupAllOrphanedGifts, 450);
  }, true);
  document.addEventListener('change', (event) => {
    if (event.target.matches?.('.cart-item .quantity__input') && Number(event.target.value) === 0) scheduleCleanup(event.target);
  }, true);

  // Covers third-party cart drawers: when they remove the AIRYSS line, remove
  // any linked gift line even if their delete button does not use theme classes.
  const cleanupAllOrphanedGifts = () => {
    if (cleanupInProgress) return;
    cleanupInProgress = true;
    getShopifyCartStrict()
      .then((cart) => {
        const hasAiryssParent = cart.items.some(isAiryssParentItem);

        // Only skip cleanup right after ATC when the parent is already present
        // (avoids racing a gift that is still being linked). If the parent is
        // gone, always strip orphan gifts immediately.
        if (hasAiryssParent && Date.now() < giftProtectUntil) {
          return null;
        }

        const orphanedGifts = cart.items.filter((gift) => {
          if (!isRemovableGiftLine(gift)) return false;
          if (isRetailAccessoryOnly(gift) && !isLinkedFreeGift(gift)) return false;
          if (hasAiryssParent) {
            // Parent present: only remove gifts whose linked parent variant is missing.
            if (!isLinkedFreeGift(gift)) return false;
            return !cart.items.some((item) =>
              String(item.variant_id) === String(gift.properties?._gift_for) && !isLinkedFreeGift(item)
            );
          }
          // No AIRYSS One left — remove every free-gift / promo gift line.
          return true;
        });

        if (!orphanedGifts.length) {
          lastGiftCart = cart;
          scheduleGiftUi(cart, 0);
          return null;
        }

        // Hide the custom gift card immediately while the cart request runs.
        upcartRoot().getElementById?.(GIFT_WRAP_ID)?.remove();
        document.getElementById(GIFT_WRAP_ID)?.remove();

        return changeLines(orphanedGifts).then(() => getShopifyCartStrict()).then((fresh) => {
          lastGiftCart = fresh;
          scheduleGiftUi(fresh, 0);
          refreshCartDrawers({ soft: true });
          window.dispatchEvent(new CustomEvent('airyss:parent-removed', { detail: { cart: fresh } }));
        });
      })
      .catch((error) => console.error('Free gift cleanup failed:', error))
      .finally(() => { cleanupInProgress = false; });
  };

  const pathHasUpcartRemove = (event) => {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    return path.some((node) => {
      if (!node || node.nodeType !== 1) return false;
      const cls = String(node.className || '');
      const label = String(node.getAttribute?.('aria-label') || node.getAttribute?.('title') || '');
      if (/remove|trash|delete|close/i.test(label)) return true;
      if (/Remove|Trash|Delete/i.test(cls)) return true;
      if (node.matches?.('button, [role="button"], svg, path') && /remove|trash|delete/i.test(cls + label)) return true;
      // UpCart line trash controls near a product tile.
      if (node.closest?.('.upcart-public-cart-item, .upcart-internal-cart-item, [class*="CartItem"]')) {
        if (node.matches?.('button, [role="button"]') && (node.querySelector?.('svg') || /×|✕|x/i.test(node.textContent || ''))) {
          return true;
        }
      }
      return false;
    });
  };

  const observeUpcartShadowForGifts = () => {
    if (shadowGiftObserverBound) return;
    const root = window.upcartDocumentOrShadowRoot;
    if (!root || root === document) return;
    new MutationObserver(() => {
      if (uiPassActive) return;
      const snapshot = readUpcartCartSync() || lastGiftCart;
      hideNativeGiftLines(snapshot);
      if (window.__airyssCartCountHoldUntil && Date.now() < window.__airyssCartCountHoldUntil) {
        applyCartCountBadge(window.__airyssCartCountHoldValue);
      }
      // Rebuilding the gift card on every UpCart mutation remounts images on iOS.
      if (Date.now() < stableUiUntil) return;
      scheduleGiftUi(snapshot, 80);
    }).observe(root, { childList: true, subtree: true });
    shadowGiftObserverBound = true;
    scheduleGiftUi(readUpcartCartSync() || lastGiftCart, 0);
  };

  // UI only — never wait on cleanup/BXGY before showing the gift card.
  const paintGiftUiFast = () => {
    if (window.__airyssCartCountHoldUntil && Date.now() < window.__airyssCartCountHoldUntil) {
      applyCartCountBadge(window.__airyssCartCountHoldValue, { skipClose: true });
    }
    const instant = readUpcartCartSync() || lastGiftCart;
    if (instant) {
      hideNativeGiftLines(instant);
      scheduleGiftUi(instant, 0);
      return;
    }
    getCart()
      .then((cart) => {
        lastGiftCart = cart;
        scheduleGiftUi(cart, 0);
      })
      .catch(() => scheduleGiftUi(null, 0));
  };

  const runGiftMaintenance = () => {
    if (bxgyRepairInProgress || cleanupInProgress) return;
    paintGiftUiFast();
    if (Date.now() < giftProtectUntil) {
      window.clearTimeout(deferredMaintenanceTimer);
      deferredMaintenanceTimer = window.setTimeout(() => {
        if (Date.now() >= giftProtectUntil) runGiftMaintenance();
      }, Math.max(80, giftProtectUntil - Date.now()));
      return;
    }
    ensureGiftForOpenCart();
    cleanupAllOrphanedGifts();
    dedupeFreeGifts();
    clampFreeGiftQuantities();
    repairBxgyDiscountConflict().then(() => {
      paintGiftUiFast();
      return dedupeFreeGifts();
    });
  };

  const scheduleGiftMaintenance = (delay = 40) => {
    // Paint first on the same turn, maintain shortly after.
    paintGiftUiFast();
    window.clearTimeout(maintenanceTimer);
    maintenanceTimer = window.setTimeout(runGiftMaintenance, delay);
  };

  const bindUpcartGiftApi = () => {
    if (upcartGiftApiBound) return;
    const names = [
      'upcartSubscribeCartOpened',
      'upcartSubscribeCartLoaded',
      'upcartSubscribeCartUpdated',
      'upcartSubscribeAddedToCart',
      'upcartSubscribeItemRemoved',
    ];
    const available = names.filter((name) => typeof window[name] === 'function');
    if (!available.length) return;
    available.forEach((name) => {
      try {
        window[name](() => {
          paintGiftUiFast();
          ensureUpcartCloseVisible();
          if (name.includes('ItemRemoved')) {
            // Parent may have been deleted — strip orphan gifts right away.
            window.setTimeout(cleanupAllOrphanedGifts, 80);
            window.setTimeout(cleanupAllOrphanedGifts, 400);
            window.setTimeout(cleanupAllOrphanedGifts, 900);
          }
          if (Date.now() < giftProtectUntil || Date.now() < stableUiUntil) {
            scheduleGiftUi(readUpcartCartSync() || lastGiftCart, 0);
            return;
          }
          if (name.includes('Opened') || name.includes('Loaded') || name.includes('AddedToCart')) {
            ensureGiftForOpenCart();
          }
          scheduleGiftMaintenance(name.includes('Opened') || name.includes('Loaded') ? 0 : 40);
        });
      } catch (error) { /* ignore */ }
    });
    upcartGiftApiBound = true;
  };

  // Hunt UpCart shadow root quickly on slow devices.
  let shadowHuntBound = false;
  const huntUpcartShadow = () => {
    if (shadowHuntBound) return;
    shadowHuntBound = true;
    let hunts = 0;
    const timer = window.setInterval(() => {
      hunts += 1;
      observeUpcartShadowForGifts();
      bindUpcartGiftApi();
      if (shadowGiftObserverBound || hunts > 80) window.clearInterval(timer);
    }, 100);
  };

  huntUpcartShadow();
  bindUpcartGiftApi();

  window.setInterval(() => {
    bindUpcartGiftApi();
    observeUpcartShadowForGifts();
  }, 6000);

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (...args) => {
    const response = await originalFetch(...args);
    const requestUrl = String(args[0]?.url || args[0] || '');
    if (/\/cart\/(add|change|update|clear)/.test(requestUrl) && !bxgyRepairInProgress && !cleanupInProgress) {
      paintGiftUiFast();
      if (Date.now() < giftProtectUntil) {
        scheduleGiftUi(readUpcartCartSync() || lastGiftCart, 0);
      } else {
        scheduleGiftMaintenance(0);
      }
    }
    return response;
  };

  document.addEventListener('click', (event) => {
    if (event.target.closest?.('.upcart-cart, [class*="upcart"], cart-drawer, #CartDrawer, [aria-controls*="cart" i], [class*="cart-icon" i]')) {
      paintGiftUiFast();
      if (Date.now() < giftProtectUntil) return;
      scheduleGiftMaintenance(0);
    }
  }, true);

  // Prefetch cart so first drawer open can paint instantly.
  getCart().then((cart) => {
    lastGiftCart = cart;
    scheduleGiftUi(cart, 0);
  }).catch(() => {});
  paintGiftUiFast();
  scheduleGiftMaintenance(0);
})();
